import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  clearLoginMessage,
  forgotPasswordThunk,
  initializeAuth,
  loginThunk,
  resetPasswordThunk,
  selectIsActionLoading,
  selectIsLoading,
  selectLoginState,
  setLoginState as setLoginStateAction,
  signOutThunk,
  signUpThunk,
  verifyLoginTwoFaThunk,
} from "../../features/auth/store/authSlice";

const RESET_EMAIL_STORAGE_KEY = "pbx_reset_email";

export const useAuth = () => {
  // This hook intentionally preserves the old component-facing API while delegating to Redux.
  const dispatch = useDispatch();
  const loginState = useSelector(selectLoginState);
  const isActionLoading = useSelector(selectIsActionLoading);
  const isLoading = useSelector(selectIsLoading);

  const setLoginState = useCallback(
    (valueOrUpdater) => {
      if (typeof valueOrUpdater === "function") {
        dispatch(setLoginStateAction(valueOrUpdater(loginState)));
        return;
      }

      dispatch(setLoginStateAction(valueOrUpdater));
    },
    [dispatch, loginState],
  );

  const handleSignUp = async (event, formState, inputHandler) => {
    event.preventDefault();

    if (
      formState.inputs.password.value !==
      formState.inputs.confirm_password.value
    ) {
      setLoginState({ state: "signup", message: "Passwords do not match." });
      return;
    }

    const clientId = Number(formState.inputs.client_id?.value);
    if (!Number.isFinite(clientId) || clientId <= 0) {
      setLoginState({ state: "signup", message: "Please select an organization." });
      return;
    }

    const action = await dispatch(
      signUpThunk({
        firstname: formState.inputs.firstname.value,
        lastname: formState.inputs.lastname.value,
        email: formState.inputs.email.value,
        password: formState.inputs.password.value,
        client_id: clientId,
      }),
    );

    if (signUpThunk.fulfilled.match(action)) {
      inputHandler("email", "", false);
      inputHandler("password", "", false);
      inputHandler("confirm_password", "", false);
      inputHandler("client_id", "", false);
    }
  };

  const handleLogin = async (event, loginFormState, loginInputHandler) => {
    event.preventDefault();
    const action = await dispatch(
      loginThunk({
        email: loginFormState.inputs.email.value,
        password: loginFormState.inputs.password.value,
      }),
    );

    if (loginThunk.fulfilled.match(action)) {
      loginInputHandler("email", "", false);
      loginInputHandler("password", "", false);
    }
  };

  const handleLoginTwoFaVerification = async (event, tokenInput, clearTokenInput) => {
    event.preventDefault();

    const code = String(tokenInput || "").replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      setLoginState({ state: "two_fa_login", message: "Please enter a valid 6-digit code." });
      return;
    }

    const action = await dispatch(verifyLoginTwoFaThunk({ token: code }));
    if (verifyLoginTwoFaThunk.fulfilled.match(action) && typeof clearTokenInput === "function") {
      clearTokenInput();
    }
  };

  const handleSignOut = async () => {
    await dispatch(signOutThunk());
  };

  // Called from App bootstrap to resolve persisted auth into a valid runtime session.
  const currentAuthenticatedUser = useCallback(async () => {
    await dispatch(initializeAuth());
  }, [dispatch]);

  const handleForgotPassword = async (event, forgotFormState) => {
    event.preventDefault();

    const email = forgotFormState?.inputs?.email?.value?.trim();
    if (!email) {
      setLoginState({ state: "forgot", message: "Please enter your email address." });
      return;
    }

    const action = await dispatch(forgotPasswordThunk({ email }));

    if (forgotPasswordThunk.fulfilled.match(action)) {
      localStorage.setItem(RESET_EMAIL_STORAGE_KEY, email);
    }
  };

  const handleResetPassword = async (event, resetFormState, resetInputHandler) => {
    event.preventDefault();
    if (
      resetFormState.inputs.password.value !==
      resetFormState.inputs.confirm_password.value
    ) {
      setLoginState({ state: "reset", message: "Passwords do not match." });
      return;
    }

    const token = new URLSearchParams(window.location.search).get("token");
    if (!token) {
      setLoginState({
        state: "reset",
        message: "Reset link is invalid or expired. Please request a new one.",
      });
      return;
    }

    const action = await dispatch(
      resetPasswordThunk({
        email: resetFormState.inputs.email.value,
        token,
        new_password: resetFormState.inputs.password.value,
      }),
    );

    if (resetPasswordThunk.fulfilled.match(action)) {
      localStorage.removeItem(RESET_EMAIL_STORAGE_KEY);
      resetInputHandler("email", "", false);
      resetInputHandler("password", "", false);
      resetInputHandler("confirm_password", "", false);
    }
  };

  const handleSignUpConfirmation = async (event) => {
    event.preventDefault();
    setLoginState({ state: "login", message: null });
  };

  useEffect(() => {
    if (!loginState?.message) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      dispatch(clearLoginMessage());
    }, 5000);

    return () => clearTimeout(timeoutId);
  }, [dispatch, loginState?.message]);

  return {
    loginState,
    setLoginState,
    isActionLoading,
    isLoading,
    handleSignUp,
    handleSignUpConfirmation,
    handleLogin,
    handleLoginTwoFaVerification,
    handleSignOut,
    currentAuthenticatedUser,
    handleForgotPassword,
    handleResetPassword,
  };
};
